import { z } from 'zod';
import {
  ApiPath,
  periodeSchema,
  kalenderKerjaSchema,
  absensiSchema,
  absensiStatusSchema,
  rekapKehadiranSchema,
  riwayatAbsensiResponseSchema,
  rekapAbsensiRowSchema,
  absensiDetailSchema,
  type CreatePeriodeRequest,
  type UpdatePeriodeRequest,
  type CreateKalenderKerjaRequest,
  type UpdateKalenderKerjaRequest,
  type StatusKehadiran,
} from '@app/shared';
import { apiGet, apiPost, apiPatch, apiPut, apiDelete, apiList } from '../../lib/api';

// --- Periode Absensi ---

export async function getPeriodeList(params?: {
  page?: number;
  limit?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}) {
  return apiList(ApiPath.ABSENSI.PERIODE, periodeSchema, params);
}

export async function getPeriode(id: number) {
  return apiGet(`${ApiPath.ABSENSI.PERIODE}/${id}`, periodeSchema);
}

export async function createPeriode(body: CreatePeriodeRequest) {
  return apiPost(ApiPath.ABSENSI.PERIODE, body, periodeSchema);
}

export async function updatePeriode(id: number, body: UpdatePeriodeRequest) {
  return apiPatch(`${ApiPath.ABSENSI.PERIODE}/${id}`, body, periodeSchema);
}

export async function deletePeriode(id: number) {
  return apiDelete(`${ApiPath.ABSENSI.PERIODE}/${id}`);
}

export async function lockPeriode(id: number, alasan: string) {
  return apiPost(`${ApiPath.ABSENSI.PERIODE}/${id}/lock`, { alasan }, periodeSchema);
}

export async function unlockPeriode(id: number, alasan: string) {
  return apiPost(`${ApiPath.ABSENSI.PERIODE}/${id}/unlock`, { alasan }, periodeSchema);
}

// --- Kalender Kerja ---

export async function getKalenderKerjaList(params?: {
  page?: number;
  limit?: number;
  search?: string;
  sekolah_id?: number;
  jenis?: string;
  bulan?: number;
  tahun?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}) {
  return apiList(ApiPath.ABSENSI.KALENDER, kalenderKerjaSchema, params);
}

export async function getKalenderKerja(id: number) {
  return apiGet(`${ApiPath.ABSENSI.KALENDER}/${id}`, kalenderKerjaSchema);
}

export async function createKalenderKerja(body: CreateKalenderKerjaRequest) {
  return apiPost(ApiPath.ABSENSI.KALENDER, body, kalenderKerjaSchema);
}

export async function updateKalenderKerja(id: number, body: UpdateKalenderKerjaRequest) {
  return apiPatch(`${ApiPath.ABSENSI.KALENDER}/${id}`, body, kalenderKerjaSchema);
}

export async function deleteKalenderKerja(id: number) {
  return apiDelete(`${ApiPath.ABSENSI.KALENDER}/${id}`);
}

// --- Absensi (Check In / Check Out) ---

export async function checkIn() {
  return apiPost(ApiPath.ABSENSI.CHECK_IN, {}, absensiSchema);
}

export async function checkOut() {
  return apiPost(ApiPath.ABSENSI.CHECK_OUT, {}, absensiSchema);
}

export async function getAbsensiToday() {
  return apiGet(ApiPath.ABSENSI.TODAY, absensiStatusSchema);
}

export async function getAbsensiStatus() {
  return apiGet(ApiPath.ABSENSI.STATUS, absensiStatusSchema);
}

// --- Status Kehadiran (Milestone 3.3) ---

export async function getRekapKehadiran(params?: {
  page?: number;
  limit?: number;
  pegawai_id?: number;
  sekolah_id?: number;
  bulan?: number;
  tahun?: number;
  status?: StatusKehadiran;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}) {
  return apiList(ApiPath.ABSENSI.REKAP, rekapKehadiranSchema, params);
}

export async function updateStatusKehadiran(
  pegawaiSekolahId: number,
  tanggal: string,
  status: StatusKehadiran,
  keterangan?: string,
) {
  return apiPut(
    ApiPath.ABSENSI.UPDATE_STATUS,
    {
      pegawaiSekolahId,
      tanggal,
      status,
      keterangan,
    },
    absensiStatusSchema,
  );
}

export async function sendAlpha() {
  return apiPost(
    ApiPath.ABSENSI.SEND_ALPHA,
    {},
    z.object({ success: z.literal(true), message: z.string() }),
  );
}

// --- Milestone 3.4: Riwayat & Rekap Absensi ---

export async function getRiwayatAbsensi(params?: {
  page?: number;
  limit?: number;
  periodeId?: number;
  sekolahId?: number;
  status?: StatusKehadiran;
  search?: string;
  pegawaiSekolahId?: number;
}) {
  return apiGet(ApiPath.ABSENSI.RIWAYAT, riwayatAbsensiResponseSchema, params);
}

export async function getRekapAbsensi(params: {
  periodeId: number;
  sekolahId?: number;
  pegawaiSekolahId?: number;
  search?: string;
}) {
  return apiGet(ApiPath.ABSENSI.REKAP, z.array(rekapAbsensiRowSchema), params);
}

export async function getAbsensiDetail(id: number) {
  return apiGet(ApiPath.ABSENSI.DETAIL(id), absensiDetailSchema);
}

// --- Milestone 7.2: Laporan Absensi ---

export async function getLaporanAbsensi(params: {
  periodeId: number;
  sekolahId?: number;
  pegawaiId?: number;
  status?: string;
  page?: number;
  limit?: number;
  search?: string;
  sort_by?: 'nama' | 'nipNik' | 'sekolahNama' | 'hadir' | 'persentaseKehadiran';
  sort_dir?: 'asc' | 'desc';
}) {
  return apiGet(
    ApiPath.ABSENSI.LAPORAN,
    z.object({
      rows: z.array(
        z.object({
          pegawaiId: z.number(),
          pegawaiSekolahId: z.number(),
          nama: z.string(),
          nipNik: z.string(),
          sekolahNama: z.string(),
          sekolahId: z.number(),
          hadir: z.number(),
          izin: z.number(),
          sakit: z.number(),
          alpha: z.number(),
          totalHariKerja: z.number(),
          persentaseKehadiran: z.number(),
        }),
      ),
      total: z.number(),
      page: z.number(),
      limit: z.number(),
    }),
    params,
  );
}
